import {
    IIssueChartDataCreateRequest,
    IssueChartDataAnnotationUnion,
    IssueChartDataArrowAnnotation,
    IssueChartDataCreateRequestUnion,
    IssueChartDataEllipseAnnotation,
    IssueChartDataRectangleAnnotation,
    IssueChartDataTextAnnotation,
    IssueFrequencySpectrumChartDataCreateRequest,
    IssueFrequencySpectrumChartMeta,
    IssueFrequencyWaterfallChartDataCreateRequest,
    IssueFrequencyWaterfallChartMeta,
    IssueStatusType,
    IssueTimeDomainChartDataCreateRequest,
    IssueTimeDomainChartMeta,
    IssueTimeSeriesChartDataCreateRequest,
    IssueTimeSeriesChartMeta,
} from '@kcftech/prometheus-client';
import { IssueChartDataInfo } from 'pages/Desk/issues/chart_data/types';
import { viewBoxToPercentConfig } from 'shared/svg_annotations/shared/shared';
import { AnnotationConfig } from 'shared/svg_annotations/types';
import { CSSProperties } from 'styled-components';
import { tokens } from 'styles/tokens';
import {
    DefaultChartExportHeightPx,
    DefaultChartExportWidthPx,
} from 'pages/workbench/workbench_tabs/trend/charts/useTrendChartExporter';

const normalizeAngle = (deg: number) => {
    const newDeg = deg % 360;

    return newDeg > 0 ? newDeg : newDeg + 360;
};

export const getIssueChartDataAnnotation = (
    annotation: AnnotationConfig,
): IssueChartDataAnnotationUnion => {
    const p = viewBoxToPercentConfig(
        annotation,
        DefaultChartExportWidthPx,
        DefaultChartExportHeightPx,
    );
    switch (p.type) {
        case 'arrow':
            return new IssueChartDataArrowAnnotation({
                Color: p.color,
                HeadX: p.headX,
                HeadY: p.headY,
                TailX: p.tailX,
                TailY: p.tailY,
            });
        case 'ellipse':
            return new IssueChartDataEllipseAnnotation({
                Color: p.color,
                X1: p.x1,
                Y1: p.y1,
                X2: p.x2,
                Y2: p.y2,
            });
        case 'rectangle':
            return new IssueChartDataRectangleAnnotation({
                Color: p.color,
                X1: p.x1,
                Y1: p.y1,
                X2: p.x2,
                Y2: p.y2,
            });
        case 'text':
            return new IssueChartDataTextAnnotation({
                Color: p.color,
                X: p.x,
                Y: p.y,
                PinAngle: normalizeAngle(p.angleDegrees),
                PinNumber: p.number,
                PinTextColor: p.textColor,
                Text: p.text,
            });
        default:
            throw new Error('Invalid annotation type');
    }
};

export const getChartDataCreateRequest = (
    info: IssueChartDataInfo,
    canSetFaultStatus: boolean,
): IssueChartDataCreateRequestUnion => {
    const common: IIssueChartDataCreateRequest = {
        Title: info.title,
        SvgData: info.svgXml,
        ChartAnnotations: info.annotations.map(getIssueChartDataAnnotation),
        FaultStatus: canSetFaultStatus ? info.faultStatus : undefined,
    };

    switch (info.meta.type) {
        case 'frequency-spectrum':
            return new IssueFrequencySpectrumChartDataCreateRequest({
                ...common,
                ChartMeta: new IssueFrequencySpectrumChartMeta(info.meta.meta),
            });
        case 'frequency-waterfall':
            return new IssueFrequencyWaterfallChartDataCreateRequest({
                ...common,
                ChartMeta: new IssueFrequencyWaterfallChartMeta(info.meta.meta),
            });
        case 'time-domain':
            return new IssueTimeDomainChartDataCreateRequest({
                ...common,
                ChartMeta: new IssueTimeDomainChartMeta(info.meta.meta),
            });
        case 'time-series':
            return new IssueTimeSeriesChartDataCreateRequest({
                ...common,
                ChartMeta: new IssueTimeSeriesChartMeta(info.meta.meta),
            });
        default:
            throw new Error('Invalid meta type');
    }
};

export const allowedHealthRangeIssueStatuses = [
    IssueStatusType.New,
    IssueStatusType.Open,
    IssueStatusType.Closed,
];

export const dropdownTriggerStyles: CSSProperties = {
    marginTop: 5,
    borderRadius: 3,
    fontSize: '1.4rem',
    backgroundColor: tokens.background.paperElevation2,
    border: `1px solid ${tokens.input.border}`,
};
